import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import {
  Customer, Project, Resource, ResourceAllocation, TimesheetEntry,
  ResourceRoleType, Holiday, Partner, PartnerRateSheet, PartnerRateSheetEntry,
  ProjectProfitability, NonBillableSummary, TimeOffRequest, OrgManagerMember
} from '../types'
import { dataService } from '../services/dataService'
import { currencyService } from '../services/currencyService'
import { reportingService } from '../services/reportingService'
import { departmentFilterService, DepartmentAccess } from '../services/departmentFilterService'
import { timesheetService, TimesheetEntryUI } from '../services/timesheetService'
import { dateCalculationService } from '../services/dateCalculationService'
import { normalizeAllocations, normalizeProjects, normalizeTimesheetEntries } from '../utils/dataNormalizer'
interface AppContextValue {
  customers: Customer[]
  allCustomers: Customer[]
  projects: Project[]
  allProjects: Project[]
  resources: Resource[]
  allocations: ResourceAllocation[]
  allAllocations: ResourceAllocation[]
  timesheets: TimesheetEntry[]
  timesheetEntriesV2: TimesheetEntryUI[]
  resourceRoleTypes: ResourceRoleType[]
  holidays: Holiday[]
  timeOffRequests: TimeOffRequest[]
  orgManagers: OrgManagerMember[]
  pendingApprovalsForMe: TimeOffRequest[]
  partners: Partner[]
  partnerRateSheets: PartnerRateSheet[]
  partnerRateSheetEntries: PartnerRateSheetEntry[]
  projectProfitabilityReports: ProjectProfitability[]
  nonBillableReports: NonBillableSummary[]
  departmentAccess: DepartmentAccess | null
  loading: boolean
  refreshData: () => Promise<void>
  setCustomers: (c: Customer[]) => void
  setProjects: (p: Project[]) => void
  setAllocations: (a: ResourceAllocation[]) => void
  resetCustomers: () => void
  resetProjects: () => void
  resetAllocations: () => void
  canAccessAllDepartments: () => boolean
  canAccessDepartment: (deptId: string | null | undefined) => boolean
  isDepartmentOwner: () => boolean
  getResourceTimeOffDates: (resourceId: string, rangeStart?: string, rangeEnd?: string) => Set<string>
}

const AppContext = createContext<AppContextValue | null>(null)

export function AppProvider({ orgId, userId, orgMemberId, orgMemberUserType, children }: { orgId: string; userId: string; orgMemberId: string; orgMemberUserType: string; children: React.ReactNode }) {
  const [allCustomers, setAllCustomers] = useState<Customer[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [allProjects, setAllProjects] = useState<Project[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [allAllocations, setAllAllocations] = useState<ResourceAllocation[]>([])
  const [allocations, setAllocations] = useState<ResourceAllocation[]>([])
  const [resources, setResources] = useState<Resource[]>([])
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([])
  const [timesheetEntriesV2, setTimesheetEntriesV2] = useState<TimesheetEntryUI[]>([])
  const [resourceRoleTypes, setResourceRoleTypes] = useState<ResourceRoleType[]>([])
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([])
  const [orgManagers, setOrgManagers] = useState<OrgManagerMember[]>([])
  const [partners, setPartners] = useState<Partner[]>([])
  const [partnerRateSheets, setPartnerRateSheets] = useState<PartnerRateSheet[]>([])
  const [partnerRateSheetEntries, setPartnerRateSheetEntries] = useState<PartnerRateSheetEntry[]>([])
  const [projectProfitabilityReports, setProjectProfitabilityReports] = useState<ProjectProfitability[]>([])
  const [nonBillableReports, setNonBillableReports] = useState<NonBillableSummary[]>([])
  const [departmentAccess, setDepartmentAccess] = useState<DepartmentAccess | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshData = useCallback(async () => {
    console.log('[AppContext] Refreshing data for orgId:', orgId, 'userId:', userId)

    setLoading(true)
    try {
      console.log('[AppContext] Loading department access...')
      let deptAccess: DepartmentAccess
      try {
        deptAccess = await departmentFilterService.getUserDepartmentAccess(userId, orgId)
        setDepartmentAccess(deptAccess)
        console.log('[AppContext] Department access loaded:', {
          canAccessAll: deptAccess.canAccessAll,
          departmentCount: deptAccess.accessibleDepartmentIds.length,
          isDepartmentOwner: deptAccess.isDepartmentOwner
        })
      } catch (deptErr) {
        console.warn('[AppContext] Department access failed, defaulting to full access:', deptErr)
        deptAccess = { canAccessAll: true, accessibleDepartmentIds: [], isDepartmentOwner: false, ownedDepartmentIds: [] }
      }

      console.log('[AppContext] Fetching all data tables...')
      const [
        customersData, projectsData, resourcesData, allocationsData,
        timesheetsData, roleTypesData, holidaysData, partnersData,
        rateSheetsData, rateSheetEntriesData, timesheetEntriesV2Data,
        timeOffData, orgManagersData,
      ] = await Promise.all([
        dataService.getCustomers(orgId).catch(() => [] as Customer[]),
        dataService.getProjects(orgId).catch(() => [] as Project[]),
        dataService.getResources(orgId).catch(() => [] as Resource[]),
        dataService.getAllocations(orgId).catch(() => [] as ResourceAllocation[]),
        dataService.getTimesheets(orgId).catch(() => [] as TimesheetEntry[]),
        dataService.getResourceRoleTypes(orgId).catch(() => [] as ResourceRoleType[]),
        dataService.getHolidays(orgId).catch(() => [] as Holiday[]),
        dataService.getPartners(orgId).catch(() => [] as Partner[]),
        dataService.getPartnerRateSheets(orgId).catch(() => [] as PartnerRateSheet[]),
        dataService.getPartnerRateSheetEntries(orgId).catch(() => [] as PartnerRateSheetEntry[]),
        timesheetService.getOrgTimesheetEntriesV2(orgId).catch(() => [] as TimesheetEntryUI[]),
        dataService.getTimeOffRequests(orgId).catch(() => [] as TimeOffRequest[]),
        dataService.getOrgManagers(orgId).catch(() => [] as OrgManagerMember[]),
      ])

      console.log('[AppContext] Data loaded:', {
        customers: customersData.length,
        projects: projectsData.length,
        resources: resourcesData.length,
        allocations: allocationsData.length,
        timesheets: timesheetsData.length,
        roleTypes: roleTypesData.length,
        holidays: holidaysData.length,
        partners: partnersData.length,
        rateSheets: rateSheetsData.length,
        rateSheetEntries: rateSheetEntriesData.length,
      })

      console.log('[AppContext] Loading currency rates...')
      await currencyService.loadExchangeRates(orgId).catch(err => {
        console.warn('[AppContext] Currency rates failed, continuing:', err)
      })

      const normalizedAllocations = normalizeAllocations(allocationsData)
      const normalizedProjects = normalizeProjects(projectsData)
      const normalizedTimesheets = normalizeTimesheetEntries(timesheetsData)

      console.log('[AppContext] Applying department filtering...')
      const filteredCustomers = departmentFilterService.filterByDepartments(customersData, deptAccess)
      const filteredProjects = departmentFilterService.filterByDepartments(normalizedProjects, deptAccess)
      const filteredResources = departmentFilterService.filterByDepartments(resourcesData, deptAccess)
      const filteredPartners = departmentFilterService.filterByDepartments(partnersData, deptAccess)
      const filteredAllocations = departmentFilterService.filterByDepartments(normalizedAllocations, deptAccess)

      console.log('[AppContext] Filtered data:', {
        customers: filteredCustomers.length,
        projects: filteredProjects.length,
        resources: filteredResources.length,
        partners: filteredPartners.length,
        allocations: filteredAllocations.length
      })

      setAllCustomers(filteredCustomers)
      setCustomers(filteredCustomers)
      setAllProjects(filteredProjects)
      setProjects(filteredProjects)
      setAllAllocations(filteredAllocations)
      setAllocations(filteredAllocations)
      setResources(filteredResources)
      setTimesheets(normalizedTimesheets)
      setTimesheetEntriesV2(timesheetEntriesV2Data)
      setResourceRoleTypes(roleTypesData)
      setHolidays(holidaysData)
      setTimeOffRequests(timeOffData)
      setOrgManagers(orgManagersData)
      setPartners(filteredPartners)
      setPartnerRateSheets(rateSheetsData)
      setPartnerRateSheetEntries(rateSheetEntriesData)

      console.log('[AppContext] Generating reports...')
      const profitability = reportingService.getProjectProfitability(
        filteredProjects, filteredResources, filteredAllocations, roleTypesData, holidaysData, filteredCustomers, timesheetsData, timeOffData
      )
      const nonBillable = reportingService.getNonBillableSummary(
        filteredProjects, filteredResources, filteredAllocations, roleTypesData, holidaysData, timesheetsData, timeOffData
      )
      setProjectProfitabilityReports(profitability)
      setNonBillableReports(nonBillable)

      console.log('[AppContext] Data refresh complete')
    } catch (err) {
      console.error('[AppContext] Error loading data:', err)
      console.error('[AppContext] Error details:', {
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
      })
    } finally {
      setLoading(false)
    }
  }, [orgId, userId])

  const resetCustomers = () => setCustomers(allCustomers)
  const resetProjects = () => setProjects(allProjects)
  const resetAllocations = () => setAllocations(allAllocations)

  const getResourceTimeOffDates = useCallback((resourceId: string, rangeStart?: string, rangeEnd?: string): Set<string> => {
    return dateCalculationService.getResourceTimeOffDateSet(resourceId, timeOffRequests, rangeStart, rangeEnd)
  }, [timeOffRequests])

  // Pending requests that the current user should approve:
  // - requests where the resource's manager_id matches the current user's id, OR
  // - requests where the resource has no manager assigned AND the current user is a manager/admin
  const isApprover = ['owner', 'admin', 'manager'].includes(orgMemberUserType)
  const pendingApprovalsForMe = useMemo((): TimeOffRequest[] => {
    if (!isApprover) return []
    return timeOffRequests.filter(req => {
      if (req.status !== 'pending') return false
      const resource = resources.find(r => r.id === req.resource_id)
      if (!resource) return false
      if (resource.manager_id) {
        return resource.manager_id === userId
      }
      // No manager assigned — all managers/admins see it
      return true
    })
  }, [timeOffRequests, resources, userId, isApprover])

  const canAccessAllDepartments = () => departmentAccess?.canAccessAll ?? false
  const canAccessDepartment = (deptId: string | null | undefined) =>
    departmentFilterService.canAccessDepartment(deptId, departmentAccess ?? { canAccessAll: false, accessibleDepartmentIds: [], isDepartmentOwner: false, ownedDepartmentIds: [] })
  const isDepartmentOwner = () => departmentAccess?.isDepartmentOwner ?? false

  useEffect(() => {
    console.log('[AppContext] Component mounted, triggering initial data load')
    refreshData()
  }, [refreshData])

  return (
    <AppContext.Provider value={{
      customers, allCustomers, projects, allProjects,
      resources, allocations, allAllocations,
      timesheets, timesheetEntriesV2, resourceRoleTypes, holidays, timeOffRequests,
      orgManagers, pendingApprovalsForMe,
      partners, partnerRateSheets, partnerRateSheetEntries,
      projectProfitabilityReports, nonBillableReports,
      departmentAccess,
      loading, refreshData,
      setCustomers, setProjects, setAllocations,
      resetCustomers, resetProjects, resetAllocations,
      canAccessAllDepartments, canAccessDepartment, isDepartmentOwner,
      getResourceTimeOffDates,
    }}>
      {children}
    </AppContext.Provider>
  )
}

export function useAppData() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useAppData must be used within AppProvider')
  return ctx
}
